(() => {
  'use strict';
  const root = document.querySelector('[data-gto-live-catalog]');
  if (!root) return;
  const list = root.querySelector('.gr-list');
  const search = root.querySelector('#gto-review-search');
  const sport = root.querySelector('#gto-review-sport');
  const availability = root.querySelector('#gto-review-availability');
  const count = root.querySelector('#gto-review-count');
  const empty = root.querySelector('#gto-review-empty');
  const status = root.querySelector('#gto-catalog-load-status');
  const retry = root.querySelector('[data-gto-retry]');
  const pagers = [...root.querySelectorAll('.gr-pagination')];
  const serverPager = root.querySelector('.gr-server-pagination');
  const normalize = value => value.normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  const rank = card => ({ available: 0, reserved: 1, tbd: 2, unavailable: 3, sold: 4 })[card.dataset.gtoStatus] ?? 3;
  const collator = new Intl.Collator('en-US', { numeric: true, sensitivity: 'base' });
  const size = 48;
  let cards = [...list.querySelectorAll('.gr-card')];
  let matches = [];
  let page = 1;
  let loading = false;
  let complete = false;
  let checkedAt = 0;
  const checkingAdds = new Set();
  const freshForms = new WeakSet();
  const params = new URLSearchParams(window.location.search);
  search.value = params.get('q') || '';
  if ([...availability.options].some(option => option.value === params.get('availability'))) availability.value = params.get('availability');

  function sectionUrl(path, sectionId, pageNumber, pageParam = 'page') {
    const url = new URL(path, window.location.origin);
    url.searchParams.set('section_id', sectionId);
    if (pageNumber) url.searchParams.set(pageParam, String(pageNumber));
    if (params.has('preview_theme_id')) url.searchParams.set('preview_theme_id', params.get('preview_theme_id'));
    return url;
  }
  async function htmlRequest(url) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15000);
    try {
      const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', signal: controller.signal });
      if (!response.ok) throw new Error('Catalog response unavailable');
      return new DOMParser().parseFromString(await response.text(), 'text/html');
    } finally { clearTimeout(timeout); }
  }
  function rebuildSports() {
    const selected = complete ? sport.value : sport.value || params.get('sport') || params.get('filter.p.m.gto.sport') || '';
    const counts = new Map();
    cards.forEach(card => { const value = card.dataset.sport; if (value) counts.set(value, (counts.get(value) || 0) + 1); });
    const options = [new Option('All cards', '')];
    [...counts.keys()].sort(collator.compare).forEach(value => options.push(new Option(`${value} (${counts.get(value)})`, value)));
    sport.replaceChildren(...options);
    sport.value = counts.has(selected) ? selected : '';
  }
  function show(moveFocus = false) {
    const pages = Math.max(1, Math.ceil(matches.length / size));
    page = Math.min(Math.max(1, page), pages);
    cards.forEach(card => { card.hidden = true; });
    const start = (page - 1) * size;
    const visible = matches.slice(start, start + size);
    visible.forEach(card => { card.hidden = false; });
    count.textContent = matches.length ? `${start + 1}–${start + visible.length} of ${matches.length} matching cards (${cards.length} total)` : `0 matching cards (${cards.length} total)`;
    empty.hidden = matches.length > 0;
    pagers.forEach(pager => {
      pager.hidden = matches.length <= size;
      pager.querySelector('[data-page-action="previous"]').disabled = page === 1;
      pager.querySelector('[data-page-action="next"]').disabled = page === pages;
      pager.querySelector('[data-page-status]').textContent = `Page ${page} of ${pages}`;
      const picker = pager.querySelector('[data-page-picker]');
      if (picker.options.length !== pages) picker.replaceChildren(...Array.from({ length: pages }, (_, index) => new Option(String(index + 1), String(index + 1))));
      picker.value = String(page);
    });
    if (moveFocus) { count.focus({ preventScroll: true }); root.scrollIntoView({ block: 'start' }); }
  }
  function filter(reset = true) {
    const terms = normalize(search.value).trim().split(/\s+/).filter(Boolean);
    matches = cards.filter(card => {
      const searchable = normalize([...card.querySelectorAll('.gr-sku,.gr-title,details')].map(node => node.textContent).join(' '));
      return (!sport.value || card.dataset.sport === sport.value) && (!availability.value || card.dataset.gtoStatus === availability.value) && terms.every(term => searchable.includes(term));
    });
    if (reset) page = 1;
    show();
  }
  function install(current) {
    const openDetails = new Set(cards.filter(card => card.querySelector('details')?.open).map(card => card.dataset.gtoProductId));
    cards = current.sort((a, b) => rank(a) - rank(b) || collator.compare(a.dataset.gtoSku, b.dataset.gtoSku));
    cards.forEach(card => { const details = card.querySelector('details'); if (details && openDetails.has(card.dataset.gtoProductId)) details.open = true; });
    list.replaceChildren(...cards);
    rebuildSports();
    filter(false);
  }
  async function loadAll(refresh = false) {
    if (loading || checkingAdds.size) return;
    loading = true;
    root.setAttribute('aria-busy', 'true');
    retry.hidden = true;
    if (!complete) {
      root.setAttribute('data-gto-initial-loading', '');
      status.textContent = 'Loading all cards for search and filters…';
    }
    try {
      let total = Number(root.dataset.total);
      let sourcePages = Number(root.dataset.pages);
      let pageParam = root.dataset.pageParam || 'page';
      const collected = new Map();
      if (!refresh) cards.forEach(card => collected.set(card.dataset.gtoProductId, card));
      const initialPage = Number(root.dataset.page) || 1;
      async function collectPage(sourcePage) {
        const doc = await htmlRequest(sectionUrl(root.dataset.collectionUrl, root.dataset.feedSection, sourcePage, pageParam));
        const feed = doc.querySelector('[data-gto-catalog-feed]');
        if (!feed || Number(feed.dataset.page) !== sourcePage) throw new Error('Catalog pagination incomplete');
        if (refresh && sourcePage === 1) { total = Number(feed.dataset.total); sourcePages = Number(feed.dataset.pages); pageParam = feed.dataset.pageParam || 'page'; }
        if (Number(feed.dataset.total) !== total || Number(feed.dataset.pages) !== sourcePages) throw new Error('Catalog changed during refresh');
        feed.querySelectorAll('.gr-card').forEach(card => { if (!card.dataset.gtoProductId || collected.has(card.dataset.gtoProductId)) throw new Error('Duplicate catalog record'); collected.set(card.dataset.gtoProductId, card); });
      }
      // Refresh page one first so every later request uses its current pagination.
      if (refresh) await collectPage(1);
      const remaining = [];
      for (let sourcePage = 1; sourcePage <= sourcePages; sourcePage++) {
        if (sourcePage !== (refresh ? 1 : initialPage)) remaining.push(sourcePage);
      }
      // Bound concurrency; reveal the grid only after the complete set is validated.
      for (let offset = 0; offset < remaining.length; offset += 4) {
        await Promise.all(remaining.slice(offset, offset + 4).map(collectPage));
      }
      if (collected.size !== total) throw new Error('Catalog count incomplete');
      install([...collected.values()]);
      complete = true;
      checkedAt = Date.now();
      root.dataset.total = String(total); root.dataset.pages = String(sourcePages);
      [search, sport, availability].forEach(control => { control.disabled = false; });
      serverPager.hidden = true;
      status.textContent = '';
    } catch (_) {
      status.textContent = complete ? 'The catalog could not refresh. Reload to confirm current prices and availability.' : 'The full catalog could not load. Use the page links below or retry; search and filters will be available once every card has loaded.';
      retry.hidden = false;
    } finally {
      loading = false;
      root.removeAttribute('aria-busy');
      root.removeAttribute('data-gto-initial-loading');
    }
  }
  search.addEventListener('input', () => filter());
  sport.addEventListener('change', () => filter());
  availability.addEventListener('change', () => filter());
  pagers.forEach(pager => {
    pager.querySelector('[data-page-action="previous"]').addEventListener('click', () => { page--; show(true); });
    pager.querySelector('[data-page-action="next"]').addEventListener('click', () => { page++; show(true); });
    pager.querySelector('[data-page-picker]').addEventListener('change', event => { page = Number(event.target.value); show(true); });
  });
  retry.addEventListener('click', () => loadAll(true));
  const refreshIfDue = () => { if (!document.hidden && complete && Date.now() - checkedAt >= 60000 && !root.querySelector('button[aria-disabled="true"]')) loadAll(true); };
  window.addEventListener('focus', refreshIfDue);
  document.addEventListener('visibilitychange', refreshIfDue);
  setInterval(refreshIfDue, 60000);

  const dialog = document.getElementById('gto-review-zoom');
  const large = dialog.querySelector('img');
  let opener;
  root.addEventListener('click', event => {
    const button = event.target.closest('.gr-photos button');
    if (!button || !root.contains(button)) return;
    const img = button.querySelector('img');
    opener = button; large.src = img.src; large.alt = img.alt;
    dialog.showModal(); dialog.querySelector('button').focus();
  });
  dialog.querySelector('button').addEventListener('click', () => dialog.close());
  dialog.addEventListener('close', () => { if (opener?.isConnected) opener.focus(); });
  root.addEventListener('submit', async event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.closest('[data-gto-live-add]')) return;
    if (freshForms.has(form)) { freshForms.delete(form); return; }
    event.preventDefault(); event.stopImmediatePropagation();
    const card = form.closest('.gr-card');
    const id = card.dataset.gtoProductId;
    if (checkingAdds.has(id)) return;
    checkingAdds.add(id);
    status.textContent = '';
    const button = form.querySelector('button[type="submit"]'); button.disabled = true;
    try {
      const url = card.querySelector('footer a').getAttribute('href');
      const doc = await htmlRequest(sectionUrl(url, root.dataset.productSection));
      const fresh = doc.querySelector('[data-gto-catalog-product] .gr-card');
      if (!fresh || fresh.dataset.gtoProductId !== id || fresh.dataset.gtoSku !== card.dataset.gtoSku) throw new Error('Card check unavailable');
      const index = cards.indexOf(card); cards[index] = fresh;
      card.replaceWith(fresh);
      if (fresh.dataset.gtoStatus !== 'available') { status.textContent = 'This card is no longer available to add. Please review its current card details.'; filter(false); return; }
      const freshForm = fresh.querySelector('form');
      if (!freshForm) throw new Error('Card form unavailable');
      freshForms.add(freshForm); freshForm.requestSubmit();
    } catch (_) {
      status.textContent = 'Current purchase eligibility could not be confirmed. Please reload and try again.';
      if (button.isConnected) button.disabled = false;
    } finally { checkingAdds.delete(id); }
  }, true);
  clearTimeout(root.gtoCatalogBootTimer);
  loadAll();
})();
